import { useCallback, useState } from "react"
import styled from "styled-components"
import {
  Button,
  Form,
  FormField,
  Icon,
  Input,
  Label,
  OutlineButton,
} from "~/components"
import { useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { trpc } from "~/utils/trpc"
import { validateEmail } from "~/utils/validation"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 2rem;
`

const Card = styled.div`
  width: 392px;
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};
  border-radius: 8px;
  padding: 32px;
`

const Title = styled.h4`
  margin: 0;
  margin-bottom: 1.5rem;
`

const InputWrapper = styled.div`
  display: flex;
`

const Trash = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  width: 30px;
  cursor: pointer;
  color: ${(props) => props.theme.colors.gray50};

  &:hover {
    color: ${(props) => props.theme.colors.error30};
  }
`

const Buttons = styled.div`
  display: flex;
  margin-top: 1.5rem;
  flex-direction: column;
  gap: 1.5rem;
`

export type AddUserProps = {}

const AddUser: React.FC<AddUserProps> = () => {
  const [emails, setEmails] = useState<string[]>([""])
  const [emailErrors, setEmailErrors] = useState<{
    [index: number]: string | null
  }>({})

  const translations = useTranslate({
    inviteUsers: "add-user.invite-users",
    addMoreUsers: "add-users.add-more-users",
    sendInvite: "add-users.send-invite",
    email: "common.email",
    invalidEmail: "errors.invalid-email-address",
    success: "add-users.toasts.success",
  })

  const organizationMembership = useOrganizationMembership()!
  const emailValidator = validateEmail(translations.invalidEmail)

  const toast = useToast()
  const { getToastInfo } = useErrorHandling()

  const { mutate: inviteUsers, isPending: isLoading } =
    trpc.organizations.inviteUsers.useMutation({
      onSuccess: () => {
        toast("success", translations.success)
        setEmails([])
        setEmailErrors({})
      },
      onError(error) {
        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )
        toast(type, message)
      },
    })

  const submit = useCallback(() => {
    if (emails.length === 0) {
      return
    }

    if (emails.some((email) => emailValidator(email) !== null)) {
      return
    }

    inviteUsers({
      id: organizationMembership.organizationId,
      emails,
    })
  }, [emails, emailValidator, inviteUsers, organizationMembership])

  return (
    <Layout>
      <Card>
        <Form preventDefault onSubmit={() => submit()}>
          <Title>{translations.inviteUsers}</Title>
          {emails.map((email, i) => (
            <FormField key={i}>
              <Label>{translations.email}</Label>
              <InputWrapper>
                <Input
                  value={email}
                  onChange={(value) => {
                    const newEmails = [...emails]
                    newEmails[i] = value
                    setEmails(newEmails)
                    setEmailErrors({ [i]: null })
                  }}
                  onBlur={() => {
                    const error = emailValidator(email)

                    if (error) {
                      setEmailErrors({
                        ...emailErrors,
                        [i]: error,
                      })
                    } else {
                      setEmailErrors({
                        ...emailErrors,
                        [i]: null,
                      })
                    }
                  }}
                  error={emailErrors[i]}
                  width="100%"
                />
                <Trash
                  onClick={() => {
                    setEmails(emails.filter((_, index) => index !== i))
                  }}
                >
                  <Icon icon="trash" type="solid" />
                </Trash>
              </InputWrapper>
            </FormField>
          ))}
          <Buttons>
            <OutlineButton
              type="button"
              leftIcon="plus"
              width="100%"
              onClick={() => {
                setEmails([...emails, ""])
              }}
            >
              {translations.addMoreUsers}
            </OutlineButton>
            <Button disabled={isLoading}>{translations.sendInvite}</Button>
          </Buttons>
        </Form>
      </Card>
    </Layout>
  )
}

export default AddUser
